import {
  ACTION_TYPE_LABELS,
  addDays,
  diffDays,
  dueGroup,
  dueLabel,
  formatDayFr,
  formatLongFr,
  isIsoDate,
  mondayOf,
  parseIsoDate,
  formatIsoDate,
  type Action,
  type ActionCategory,
  type ActionType,
} from '@cockpit/shared';

/**
 * Logique pure de l'écran « À faire » (lot C2, maquette `drawTodo`, `drawBoard`, `openPop`) : filtres,
 * groupes, colonnes de la semaine, raccourcis de date, liens et mises à jour optimistes. Testée sans
 * navigateur.
 */

// ------------------------------------------------------------------ Filtres

/** `all` = tout le monde, sinon l'identifiant d'un membre. */
export type WhoFilter = 'all' | string;
export type CategoryFilter = 'all' | ActionCategory;

export const CATEGORY_FILTERS: readonly { key: CategoryFilter; label: string }[] = [
  { key: 'all', label: 'Tout' },
  { key: 'projets', label: 'Projets' },
  { key: 'clients', label: 'Clients et factures' },
  { key: 'perso', label: 'Tâches ajoutées' },
];

export function matchesWho(action: Action, who: WhoFilter): boolean {
  return who === 'all' || action.assigneeId === who;
}

export function matchesCategory(action: Action, category: CategoryFilter): boolean {
  return category === 'all' || action.category === category;
}

/** Par date d'échéance, puis les réponses à traiter d'abord, puis par titre (ordre stable). */
export function sortActions(actions: readonly Action[]): Action[] {
  const weight = (a: Action) => (a.status === 'REPLY_TO_HANDLE' ? 0 : 1);
  return [...actions].sort(
    (a, b) =>
      a.dueDate.localeCompare(b.dueDate) ||
      weight(a) - weight(b) ||
      a.title.localeCompare(b.title, 'fr') ||
      a.id.localeCompare(b.id),
  );
}

export function visibleActions(
  actions: readonly Action[],
  who: WhoFilter,
  category: CategoryFilter,
): Action[] {
  return sortActions(actions.filter((a) => matchesWho(a, who) && matchesCategory(a, category)));
}

/** Compteurs du filtre « qui » (toutes catégories, comme la maquette). */
export function whoCounts(actions: readonly Action[], memberIds: readonly string[]): Record<string, number> {
  const counts: Record<string, number> = { all: actions.length };
  for (const id of memberIds) counts[id] = actions.filter((a) => a.assigneeId === id).length;
  return counts;
}

/** Compteurs du filtre de catégorie, pour la personne choisie. */
export function categoryCounts(actions: readonly Action[], who: WhoFilter): Record<CategoryFilter, number> {
  const mine = actions.filter((a) => matchesWho(a, who));
  return {
    all: mine.length,
    projets: mine.filter((a) => a.category === 'projets').length,
    clients: mine.filter((a) => a.category === 'clients').length,
    perso: mine.filter((a) => a.category === 'perso').length,
  };
}

// ------------------------------------------------------------------ Liste groupée

export type DueGroup = ReturnType<typeof dueGroup>;

export const GROUPS: readonly { key: DueGroup; label: string }[] = [
  { key: 'late', label: 'En retard' },
  { key: 'today', label: "Aujourd'hui" },
  { key: 'week', label: 'Cette semaine' },
  { key: 'later', label: 'Plus tard' },
];

/** Groupes non vides, dans l'ordre de la maquette, chacun trié. */
export function groupActions(
  actions: readonly Action[],
  today: string,
): { key: DueGroup; label: string; items: Action[] }[] {
  const sorted = sortActions(actions);
  return GROUPS.map((g) => ({
    ...g,
    items: sorted.filter((a) => dueGroup(a.dueDate, today) === g.key),
  })).filter((g) => g.items.length > 0);
}

/** « 12 actions ouvertes, dont 4 en retard et 3 pour aujourd'hui. » */
export function summaryText(actions: readonly Action[], today: string): string {
  const n = actions.length;
  if (n === 0) return 'Rien à faire pour le moment.';
  const late = actions.filter((a) => dueGroup(a.dueDate, today) === 'late').length;
  const forToday = actions.filter((a) => dueGroup(a.dueDate, today) === 'today').length;
  const s = n > 1 ? 's' : '';
  return `${n} action${s} ouverte${s}, dont ${late} en retard et ${forToday} pour aujourd'hui.`;
}

// ------------------------------------------------------------------ Puces

const TYPE_CHIP_CLASS: Record<ActionType, string> = {
  quote_followup: 'c-warn',
  invoice_create: 'c-acc',
  subscription_setup: 'c-ok',
  unpaid_reminder: 'c-bad',
  payment_failed: 'c-bad',
  review_request: 'c-ok',
  quote_to_send: 'c-acc',
  meeting: 'c-acc',
  deadline: 'c-mut',
  info: 'c-mut',
  project_step: 'c-acc',
  project_plan: 'c-acc',
  user_task: 'c-mut',
  expense_categorize: 'c-mut',
  partner_missing: 'c-mut',
  idea: 'c-acc',
  meeting_r2: 'c-warn',
  partner_unknown: 'c-mut',
  r1_prep: 'c-acc',
  quote_prepare: 'c-acc',
  support: 'c-warn',
  supplier_alert: 'c-bad',
  quote_win_chance: 'c-acc',
  mail_followup: 'c-warn',
};

/** Puce d'une action : le statut de réponse prime sur le type (maquette, `actionRow`). */
export function actionChip(action: Pick<Action, 'type' | 'status'>): { text: string; cls: string } {
  if (action.status === 'REPLY_TO_HANDLE') return { text: 'Réponse à traiter', cls: 'c-ok' };
  if (action.status === 'WAITING_REPLY') return { text: 'En attente de réponse', cls: 'c-mut' };
  return { text: ACTION_TYPE_LABELS[action.type], cls: TYPE_CHIP_CLASS[action.type] };
}

/** Libellé du bouton « Fait » selon l'action. */
export function doneLabel(action: Pick<Action, 'type' | 'status'>): string {
  if (action.status === 'REPLY_TO_HANDLE') return 'Marquer comme traitée';
  return action.type === 'info' ? 'Vu' : 'Fait';
}

// ------------------------------------------------------------------ Semaine (glisser-déposer)

export type BoardColumnKey = 'late' | 'later' | number;

export interface BoardColumn {
  key: BoardColumnKey;
  label: string;
  /** Date du jour de la colonne (colonnes de jours seulement). */
  date: string | null;
  /** « 13 oct. » sous le nom du jour. */
  sub: string | null;
}

const WEEKDAYS_LONG = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];
const MONTHS_SHORT = [
  'janv.',
  'févr.',
  'mars',
  'avr.',
  'mai',
  'juin',
  'juil.',
  'août',
  'sept.',
  'oct.',
  'nov.',
  'déc.',
];

function weekdayIndex(date: string): number {
  const { year, month, day } = parseIsoDate(date);
  const js = new Date(Date.UTC(year, month - 1, day)).getUTCDay();
  return js === 0 ? 6 : js - 1;
}

/** « En retard », aujourd'hui et les 6 jours suivants, « Plus tard » (maquette, `COLS`). */
export function boardColumns(today: string): BoardColumn[] {
  const days = [0, 1, 2, 3, 4, 5, 6].map((offset): BoardColumn => {
    const date = addDays(today, offset);
    const { day, month } = parseIsoDate(date);
    return {
      key: offset,
      label: offset === 0 ? "Aujourd'hui" : (WEEKDAYS_LONG[weekdayIndex(date)] ?? ''),
      date,
      sub: `${day} ${MONTHS_SHORT[month - 1]}`,
    };
  });
  return [
    { key: 'late', label: 'En retard', date: null, sub: null },
    ...days,
    { key: 'later', label: 'Plus tard', date: null, sub: null },
  ];
}

export function boardColumnOf(dueDate: string, today: string): BoardColumnKey {
  const offset = diffDays(today, dueDate);
  if (offset < 0) return 'late';
  return offset > 6 ? 'later' : offset;
}

/**
 * Nouvelle échéance après un dépôt dans une colonne ; null si rien ne change ou si la colonne n'accepte
 * pas de dépôt (« En retard »). « Plus tard » garde une date déjà lointaine, sinon J+7 (maquette).
 */
export function dropDate(column: BoardColumnKey, dueDate: string, today: string): string | null {
  if (column === 'late') return null;
  let target: string;
  if (column === 'later') {
    const later = addDays(today, 7);
    target = dueDate > later ? dueDate : later;
  } else {
    target = addDays(today, column);
  }
  return target === dueDate ? null : target;
}

// ------------------------------------------------------------------ Popover de date

/** Même jour du mois suivant, ramené au dernier jour du mois s'il n'existe pas (31 janv. → 28 févr.). */
export function addMonths(date: string, months: number): string {
  const { year, month, day } = parseIsoDate(date);
  const index = year * 12 + (month - 1) + months;
  const y = Math.floor(index / 12);
  const m = (index % 12) + 1;
  const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return formatIsoDate({ year: y, month: m, day: Math.min(day, last) });
}

export interface QuickDate {
  key: 'today' | 'tomorrow' | 'in3' | 'nextMonday' | 'in2w' | 'in1m';
  label: string;
  date: string;
  /** « demain », « lun. 19 oct. » */
  hint: string;
}

/** Raccourcis du popover (maquette, `openPop`). « Lundi prochain » est toujours après aujourd'hui. */
export function quickDates(today: string): QuickDate[] {
  const entries: [QuickDate['key'], string, string][] = [
    ['today', "Aujourd'hui", today],
    ['tomorrow', 'Demain', addDays(today, 1)],
    ['in3', 'Dans 3 jours', addDays(today, 3)],
    ['nextMonday', 'Lundi prochain', addDays(mondayOf(today), 7)],
    ['in2w', 'Dans 2 semaines', addDays(today, 14)],
    ['in1m', 'Dans un mois', addMonths(today, 1)],
  ];
  return entries.map(([key, label, date]) => ({ key, label, date, hint: formatDayFr(date, today) }));
}

/** Date saisie dans le popover : valide et pas avant aujourd'hui, sinon null. */
export function pickedDate(value: string, today: string): string | null {
  return isIsoDate(value) && value >= today ? value : null;
}

/** Toast après un déplacement : « Déplacée : pour mar. 13 oct. » (maquette, `moveTo`). */
export function movedToast(date: string, today: string): string {
  return `Déplacée : ${dueLabel(date, today).toLowerCase()}`;
}

// ------------------------------------------------------------------ Liens

export interface ExternalLink {
  label: string;
  url: string;
}

export const axonautCompanyUrl = (id: string) =>
  `https://axonaut.com/business/company/show/${encodeURIComponent(id)}`;
export const stripeCustomerUrl = (id: string) =>
  `https://dashboard.stripe.com/customers/${encodeURIComponent(id)}`;

/** Adresse http(s) valide, sinon null (une valeur de `payload` n'est jamais rendue telle quelle en lien). */
function httpUrl(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  try {
    const url = new URL(value);
    return url.protocol === 'https:' || url.protocol === 'http:' ? url.toString() : null;
  } catch {
    return null;
  }
}

/** Lot BR : la carte « Fixer le R3 avec … » porte le badge de son rendez-vous. */
export function actionMeetingBadge(action: Pick<Action, 'type' | 'title'>): 'R3' | 'R4' | 'R5' | null {
  if (action.type !== 'meeting') return null;
  return (/\bR[345]\b/.exec(action.title)?.[0] as 'R3' | 'R4' | 'R5' | undefined) ?? null;
}

/**
 * Liens d'une action vers les outils (maquette : « Axonaut », « Stripe », « Admin Evolusite »), seulement
 * quand l'identifiant correspondant est connu. « Admin Evolusite » : adresse calculée par le serveur à partir
 * du modèle réglé sur la connexion du back (`adminSiteUrlTemplate`).
 */
export function actionLinks(
  action: Pick<Action, 'client'> & { payload?: Action['payload'] },
): ExternalLink[] {
  const client = action.client;
  const links: ExternalLink[] = [];
  if (client?.axonautCompanyId)
    links.push({ label: 'Axonaut', url: axonautCompanyUrl(client.axonautCompanyId) });
  if (client?.stripeCustomerId)
    links.push({ label: 'Stripe', url: stripeCustomerUrl(client.stripeCustomerId) });
  if (client?.adminSiteUrl) links.push({ label: 'Admin Evolusite', url: client.adminSiteUrl });
  // Lot BG : le mail du fournisseur (carte « <Fournisseur> · <objet> »).
  const mail = httpUrl(action.payload?.mailUrl);
  if (mail) links.push({ label: 'Gmail', url: mail });
  return links;
}

/**
 * Liens du bouton principal « liens seulement » (abonnement à mettre en place : rien n'est exécuté côté
 * Stripe, l'action se ferme d'elle-même quand la synchro voit l'abonnement).
 */
export function primaryLinks(action: Pick<Action, 'client'>): ExternalLink[] {
  const client = action.client;
  const links: ExternalLink[] = [];
  if (client?.stripeCustomerId) {
    links.push({ label: 'Ouvrir le client dans Stripe', url: stripeCustomerUrl(client.stripeCustomerId) });
  } else {
    links.push({ label: 'Ouvrir le tableau de bord Stripe', url: 'https://dashboard.stripe.com/customers' });
  }
  if (client?.adminSiteUrl) {
    links.push({ label: "Ouvrir le site dans l'admin Evolusite", url: client.adminSiteUrl });
  }
  if (client?.axonautCompanyId) {
    links.push({ label: 'Ouvrir la fiche dans Axonaut', url: axonautCompanyUrl(client.axonautCompanyId) });
  }
  return links;
}

// ------------------------------------------------------------------ Fait cette semaine

const WEEKDAYS_LOWER = ['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi', 'dimanche'];

/** « à l'instant », « il y a 12 min », « aujourd'hui », « hier », « vendredi », « 3 oct. ». */
export function doneWhen(doneAt: string, today: string, now: Date = new Date()): string {
  const at = new Date(doneAt);
  const minutes = Math.floor((now.getTime() - at.getTime()) / 60_000);
  const date = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Paris' }).format(at);
  const days = diffDays(date, today);
  if (days === 0 && minutes >= 0 && minutes < 2) return "à l'instant";
  if (days === 0 && minutes >= 0 && minutes < 60) return `il y a ${minutes} min`;
  if (days <= 0) return "aujourd'hui";
  if (days === 1) return 'hier';
  if (days < 7) return WEEKDAYS_LOWER[weekdayIndex(date)] ?? date;
  const { day, month } = parseIsoDate(date);
  return `${day} ${MONTHS_SHORT[month - 1]}`;
}

// ------------------------------------------------------------------ Objectif du mois

const MONTH_NAMES = [
  'janvier',
  'février',
  'mars',
  'avril',
  'mai',
  'juin',
  'juillet',
  'août',
  'septembre',
  'octobre',
  'novembre',
  'décembre',
];

/** « Objectif d'octobre », « Objectif de mars » (élision devant une voyelle). */
export function objectiveTitle(month: string): string {
  const name = MONTH_NAMES[Number(month.slice(5, 7)) - 1] ?? month;
  return /^[aeiouéèêàâîôûy]/i.test(name) ? `Objectif d'${name}` : `Objectif de ${name}`;
}

/** Jours restants dans le mois (maquette : 19 le 12 octobre). */
export function daysLeftInMonth(today: string): number {
  const { year, month, day } = parseIsoDate(today);
  return new Date(Date.UTC(year, month, 0)).getUTCDate() - day;
}

// ------------------------------------------------------------------ Mises à jour optimistes

export type ActionPatch = Partial<Pick<Action, 'dueDate' | 'assigneeId'>>;

/** Applique `patch` à l'action `id` ; rend la liste et l'action d'avant (null si absente). */
export function applyPatch(
  list: readonly Action[],
  id: string,
  patch: ActionPatch,
): { list: Action[]; previous: Action | null } {
  const previous = list.find((a) => a.id === id) ?? null;
  if (!previous) return { list: [...list], previous: null };
  return { list: list.map((a) => (a.id === id ? { ...a, ...patch } : a)), previous };
}

/**
 * Annule un patch : remet les champs d'avant, seulement s'ils portent encore la valeur optimiste (une
 * modification plus récente n'est pas écrasée).
 */
export function revertPatch(list: readonly Action[], previous: Action, patch: ActionPatch): Action[] {
  return list.map((a) => {
    if (a.id !== previous.id) return a;
    const restored = { ...a };
    for (const key of Object.keys(patch) as (keyof ActionPatch)[]) {
      if (a[key] === patch[key]) (restored as Record<string, unknown>)[key] = previous[key];
    }
    return restored;
  });
}

/**
 * Mise à jour optimiste d'une action : appliquée aussitôt, confirmée par la réponse du serveur, annulée
 * si l'appel échoue. `read`/`write` donnent accès à la liste affichée.
 */
export async function optimisticPatch(options: {
  read: () => readonly Action[];
  write: (list: Action[]) => void;
  id: string;
  patch: ActionPatch;
  commit: () => Promise<Action>;
}): Promise<{ ok: true; action: Action } | { ok: false; error: unknown }> {
  const { list, previous } = applyPatch(options.read(), options.id, options.patch);
  options.write(list);
  try {
    const action = await options.commit();
    options.write(options.read().map((a) => (a.id === action.id ? action : a)));
    return { ok: true, action };
  } catch (error) {
    if (previous) options.write(revertPatch(options.read(), previous, options.patch));
    return { ok: false, error };
  }
}

/** Retire une action de la liste (faite, écartée) et rend de quoi la remettre à sa place. */
export function removeAction(
  list: readonly Action[],
  id: string,
): { list: Action[]; restore: (current: readonly Action[]) => Action[] } {
  const removed = list.find((a) => a.id === id);
  return {
    list: list.filter((a) => a.id !== id),
    restore: (current) =>
      removed && !current.some((a) => a.id === id) ? [...current, removed] : [...current],
  };
}

// ------------------------------------------------------------------ Pastille de l'onglet

/** Pastille de l'onglet « À faire » (maquette `drawTabs`) : actions ouvertes dont l'échéance est passée. */
export function lateCount(actions: readonly Pick<Action, 'dueDate'>[], today: string): number {
  return actions.filter((a) => dueGroup(a.dueDate, today) === 'late').length;
}

// ------------------------------------------------------------------ Horizon (lot T)

/**
 * Date au-delà de laquelle une tâche est repliée sous « Plus tard » (aujourd'hui + N mois) ; null quand
 * l'horizon est coupé (null ou 0 : tout afficher).
 */
export function horizonLimit(today: string, months: number | null | undefined): string | null {
  return months ? addMonths(today, months) : null;
}

/** Sépare les tâches affichées de celles prévues après l'horizon (repliées, jamais « en retard »). */
export function splitByHorizon<T extends Pick<Action, 'dueDate'>>(
  actions: readonly T[],
  today: string,
  months: number | null | undefined,
): { shown: T[]; beyond: T[]; limit: string | null } {
  const limit = horizonLimit(today, months);
  if (!limit) return { shown: [...actions], beyond: [], limit: null };
  return {
    shown: actions.filter((a) => a.dueDate <= limit),
    beyond: actions.filter((a) => a.dueDate > limit),
    limit,
  };
}

/** « 13 janvier 2027 » (sans le jour de la semaine). */
export function longDate(date: string): string {
  return formatLongFr(date).replace(/^\S+\s/, '');
}

/** « 2 tâches prévues après le 13 janvier 2027 ». */
export function beyondText(count: number, limit: string): string {
  return `${count} tâche${count > 1 ? 's' : ''} prévue${count > 1 ? 's' : ''} après le ${longDate(limit)}`;
}

/** Libellé d'un choix d'horizon (0 = jamais). */
export function horizonLabel(months: number): string {
  return months === 0 ? 'jamais' : `${months} mois`;
}

// ------------------------------------------------------------------ « Nouveau » (lot T)

/**
 * Dernière visite de « À faire » : propre à chaque membre et à chaque navigateur (localStorage). Une carte
 * créée après la visite précédente porte « Nouveau » ; sans visite précédente connue, aucune.
 */
export const lastSeenKey = (userId: string) => `cockpit.afaire.lastSeen.${userId}`;

export function newActionIds(
  actions: readonly Pick<Action, 'id' | 'createdAt'>[],
  seenBefore: string | null,
): Set<string> {
  if (!seenBefore) return new Set();
  const since = Date.parse(seenBefore);
  if (Number.isNaN(since)) return new Set();
  return new Set(actions.filter((a) => Date.parse(a.createdAt) > since).map((a) => a.id));
}
